import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { getFirmOfficesQueryOptions } from "@/lib/data/react-query";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AddOrEditOfficeModal } from "./AddOrEditOfficeModal";
import { NoOfficesCard } from "./NoOfficesCard";
import { OfficeDetails } from "./OfficeDetails";
import { OfficesLoadingCard } from "./OfficesLoadingCard";

function FirmOfficesViewDangerous() {
  const { hasActionPermission } = useUser();
  const canCreateOffice = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmOffice,
    ACTION_PERMISSION_VALUES.create,
  );
  const {
    data: officesData,
    isLoading: isOfficesLoading,
    isError: isOfficesError,
  } = useQuery(getFirmOfficesQueryOptions());
  const offices = useMemo(() => officesData?.data ?? [], [officesData]);

  const [activeOfficeId, setActiveOfficeId] = useState(
    offices.length ? offices[0]?.id : "",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingOffice, setIsAddingOffice] = useState(false);

  const activeOffice = offices.find((office) => office.id === activeOfficeId);

  useEffect(() => {
    if (!isOfficesLoading && offices.length && !activeOfficeId) {
      setActiveOfficeId(offices[0].id);
    }
  }, [isOfficesLoading, activeOfficeId, offices]);

  return (
    <div className="container">
      {isOfficesError ? (
        <ServerErrorFallbackCard />
      ) : isOfficesLoading ? (
        <OfficesLoadingCard />
      ) : (
        <>
          <div className="flex items-center space-x-2">
            <Select value={activeOfficeId} onValueChange={setActiveOfficeId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select an office" />
              </SelectTrigger>
              <SelectContent>
                {offices.map((office) => (
                  <SelectItem key={office.id} value={office.id}>
                    {office.label}
                  </SelectItem>
                ))}
                {offices.length === 0 && (
                  <SelectItem value="no-offices" disabled>
                    No offices
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {canCreateOffice && (
              <Button
                onClick={() => {
                  setIsAddingOffice(true);
                }}
                variant="outline"
                size="md"
              >
                <PlusCircle className="h-4 w-4" />
                <Text
                  as="span"
                  variant="body"
                  className="hidden md:ml-2 md:block"
                >
                  Create office
                </Text>
              </Button>
            )}
          </div>

          <div className="mt-6 w-full md:w-3/4 lg:w-1/2">
            {offices.length === 0 ? (
              <NoOfficesCard
                canCreateOffice={canCreateOffice}
                onCreateOffice={() => {
                  setIsAddingOffice(true);
                }}
              />
            ) : (
              activeOffice && (
                <OfficeDetails
                  office={activeOffice}
                  onEdit={() => {
                    setIsEditing(true);
                  }}
                  onDelete={() => {
                    const index = offices.findIndex(
                      (office) => office.id === activeOfficeId,
                    );
                    if (offices.length <= 1) {
                      setActiveOfficeId("");
                    } else {
                      setActiveOfficeId(
                        index === 0 ? offices[1].id : offices[index - 1].id,
                      );
                    }
                  }}
                  isLoading={isOfficesLoading}
                />
              )
            )}
          </div>
        </>
      )}

      {activeOffice && (
        <AddOrEditOfficeModal
          editing={true}
          activeOffice={activeOffice}
          setOpen={setIsEditing}
          setActiveOfficeId={setActiveOfficeId}
          open={isEditing}
        />
      )}

      <AddOrEditOfficeModal
        editing={false}
        open={isAddingOffice}
        setOpen={setIsAddingOffice}
        setActiveOfficeId={setActiveOfficeId}
      />
    </div>
  );
}

export default function FirmOfficesView() {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <FirmOfficesViewDangerous />
    </ErrorBoundary>
  );
}
